import { useState } from 'react'

import {
  type IntakeFormRefusal,
  type ReplacedIntakeForm,
} from '@/definitions/intakeForm'
import { useReplaceIntakeForm } from '@/hooks/useReplaceIntakeForm'

import {
  awaitingWarningConfirmation,
  runIntakeFormSave,
} from '../utils/runIntakeFormSave'

const FORM_GONE_MESSAGE =
  'This form is no longer there — it was removed after this box was opened. ' +
  'Close the box and reload the list.'

const SAVE_UNCONFIRMED_MESSAGE =
  'The server did not answer in time, so whether the form was replaced is not known. ' +
  'Saving the same definition again is safe; to change anything else, close the box ' +
  'and reopen it.'

interface ReplaceCallbacks {
  onNameRefused: (message: string) => void
  onSaved: (saved: ReplacedIntakeForm) => void
}

interface SubmitArgs {
  displayName: string
  formTemplateJson: string
  formVersion: number
}

/**
 * The save flow behind replacing a custom form: what to send, and what each
 * refusal means.
 *
 * Nothing here decides what is acceptable. Only text that is not JSON at all is
 * judged locally; every other verdict comes from the API's refusal.
 */
export const useReplaceCustomForm = (
  orgId: string,
  baseFormId: string,
  callbacks: ReplaceCallbacks
) => {
  const replaceForm = useReplaceIntakeForm()

  const [refusal, setRefusal] = useState<IntakeFormRefusal | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const retireRefusal = () => {
    setRefusal(null)
    setProblem(null)
  }

  const warnings = refusal?.warnings ?? []
  const isAwaitingConfirmation = awaitingWarningConfirmation(refusal)

  const submit = ({ displayName, formTemplateJson, formVersion }: SubmitArgs) =>
    runIntakeFormSave({
      displayName,
      formTemplateJson,
      goneMessage: FORM_GONE_MESSAGE,
      onNameRefused: callbacks.onNameRefused,
      onSaved: callbacks.onSaved,
      save: ({ displayName: trimmed, formTemplate }) =>
        replaceForm.mutateAsync({
          orgId,
          baseFormId,
          data: {
            display_name: trimmed,
            form_template: formTemplate,
            form_version: formVersion,
            acknowledge_warnings: isAwaitingConfirmation,
          },
        }),
      setProblem,
      setRefusal,
      timeoutMessage: SAVE_UNCONFIRMED_MESSAGE,
    })

  return {
    isAwaitingConfirmation,
    isSaving: replaceForm.isPending,
    problem,
    refusal,
    retireRefusal,
    submit,
    warnings,
  }
}
